import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import DashboardLayout from "../../Layout/DashboardLayout";
import { companyID, ExportExcel } from "../../Common/UIComponents";
import {
  fetchSetupUtilReportData,
  fetchSetupUtilProjectList,
  fetchSetupUtilDumpReport,
} from "../../API_Service/Reports_Slice";

// ─── Constants ───────────────────────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [10, 25, 50];

// ─── Sub-components ──────────────────────────────────────────────────────────
function StatCard({
  label,
  value,
  valueColor,
  accent,
  progress,
  sub,
  subIcon,
  subColor,
}) {
  return (
    <div
      className={`bg-white p-6 rounded-xl shadow-sm ${accent ? "border-l-4 border-[#08757c]" : ""}`}
    >
      <p className="text-xs font-bold text-[#3e494a] uppercase mb-1">{label}</p>
      <h3 className={`text-2xl font-bold ${valueColor ?? "text-[#181c1d]"}`}>
        {value}
      </h3>
      {progress !== undefined && (
        <div className="w-full bg-[#f1f4f4] h-1.5 rounded-full mt-4">
          <div
            className="bg-[#bbf46e] h-full rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
      {sub && (
        <p
          className={`text-xs font-medium mt-2 flex items-center ${subColor ?? "text-[#3e494a]"}`}
        >
          {subIcon && (
            <span className="material-symbols-outlined text-sm mr-1">
              {subIcon}
            </span>
          )}
          {sub}
        </p>
      )}
    </div>
  );
}

function ReportRow({ record }) {
  return (
    <tr className="hover:bg-[#f1f4f4] transition-colors">
      <td className="px-5 py-2.5">
        <span className="font-bold text-[#005b60]">{record.ProjectName}</span>
      </td>
      <td className="px-5 py-2.5">
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-[#181c1d]">
            {record.FacilityName}
          </span>
          <span className="text-xs text-[#3e494a]">{record.TestTypeName}</span>
          {record.TestName && (
            <span className="text-[10px] text-[#6e797a] italic">
              {record.TestName}
            </span>
          )}
        </div>
      </td>
      <td className="px-2 py-4 text-center text-sm font-medium">
        {record.BilledHrs ?? "-"}
      </td>
      <td className="px-2 py-4 text-center text-sm font-medium">
        {record.NonBilled ?? "-"}
      </td>
      <td className="px-2 py-4 text-center text-sm font-bold text-[#08757c] bg-[#08757c]/5">
        {record.TotalHrs ?? "-"}
      </td>
      <td className="px-5 py-2.5">
        <div className="text-[11px] text-[#3e494a] space-y-1">
          <p className="flex items-center">
            <span className="material-symbols-outlined text-[14px] mr-1">
              login
            </span>
            {record.StartDate}{" "}
            <span className="ml-1 text-[#6e797a]">{record.StartTime}</span>
          </p>
          <p className="flex items-center">
            <span className="material-symbols-outlined text-[14px] mr-1">
              logout
            </span>
            {record.EndDate}{" "}
            <span className="ml-1 text-[#6e797a]">{record.EndTime}</span>
          </p>
        </div>
      </td>
      <td className="px-5 py-2.5">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <span className="material-symbols-outlined text-[#005683] text-lg">
              device_thermostat
            </span>
            <span className="text-xs">{record.TemperatureRange ?? "-"}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="material-symbols-outlined text-[#005683] text-lg">
              humidity_percentage
            </span>
            <span className="text-xs">{record.HumidityRange ?? "-"}</span>
          </div>
        </div>
      </td>
    </tr>
  );
}

function Pagination({ currentPage, totalPages, onPageChange }) {
  const { pages, showEllipsis, lastPage } = useMemo(() => {
    if (totalPages <= 5) {
      return {
        pages: Array.from({ length: totalPages }, (_, i) => i + 1),
        showEllipsis: false,
      };
    }
    return { pages: [1, 2, 3], showEllipsis: true, lastPage: totalPages };
  }, [totalPages]);

  const btnCls = (active) =>
    `w-7 h-7 flex items-center justify-center text-xs font-medium rounded-md ${
      active
        ? "bg-[#08757c] text-white font-bold"
        : "hover:bg-[#e5e9e9] text-[#181c1d]"
    }`;

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1}
        className="p-1 hover:bg-[#e5e9e9] rounded transition-colors disabled:opacity-40"
      >
        <span className="material-symbols-outlined text-sm">first_page</span>
      </button>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-1 hover:bg-[#e5e9e9] rounded transition-colors disabled:opacity-40"
      >
        <span className="material-symbols-outlined text-sm">chevron_left</span>
      </button>
      <div className="flex items-center mx-2 gap-1">
        {pages.map((p) => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={btnCls(currentPage === p)}
          >
            {p}
          </button>
        ))}
        {showEllipsis && (
          <>
            <span className="text-xs px-1 text-[#6e797a]">...</span>
            <button
              onClick={() => onPageChange(lastPage)}
              className={btnCls(currentPage === lastPage)}
            >
              {lastPage}
            </button>
          </>
        )}
      </div>
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-1 hover:bg-[#e5e9e9] rounded transition-colors disabled:opacity-40"
      >
        <span className="material-symbols-outlined text-sm">chevron_right</span>
      </button>
      <button
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages}
        className="p-1 hover:bg-[#e5e9e9] rounded transition-colors disabled:opacity-40"
      >
        <span className="material-symbols-outlined text-sm">last_page</span>
      </button>
    </div>
  );
}

// ─── Custom Project Dropdown ─────────────────────────────────────────────────
function ProjectDropdown({ projects, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  const selected = projects.find((p) => p.ProjectId === value);
  const label = selected
    ? `${selected.ProjectCode} — ${selected.ProjectName}`
    : "Select Project";

  const filtered = useMemo(() => {
    if (!query.trim()) return projects;
    const q = query.toLowerCase();
    return projects.filter(
      (p) =>
        p.ProjectName?.toLowerCase().includes(q) ||
        p.ProjectCode?.toLowerCase().includes(q),
    );
  }, [projects, query]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function select(id) {
    onChange(id);
    setOpen(false);
    setQuery("");
  }

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-[#f1f4f4] rounded-lg text-sm text-[#181c1d] focus:outline-none focus:ring-2 focus:ring-[#08757c] transition-all"
      >
        <span className="truncate">{label}</span>
        <span
          className={`material-symbols-outlined text-[18px] text-[#6e797a] transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        >
          expand_more
        </span>
      </button>

      {/* Panel */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-xl shadow-lg border border-[#e0e3e3] overflow-hidden">
          {/* Search inside dropdown */}
          <div className="p-2 border-b border-[#f1f4f4]">
            <div className="relative">
              <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-[16px] text-[#6e797a]">
                search
              </span>
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search project..."
                className="w-full pl-8 pr-3 py-1.5 bg-[#f1f4f4] rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#08757c]"
              />
            </div>
          </div>

          {/* Options list — fixed height with scroll */}
          <ul className="max-h-52 overflow-y-auto py-1">
            <li
              onClick={() => select(0)}
              className={`px-4 py-2 text-sm cursor-pointer transition-colors ${
                value === 0
                  ? "bg-[#08757c]/10 text-[#08757c] font-semibold"
                  : "hover:bg-[#f1f4f4] text-[#181c1d]"
              }`}
            >
              All Projects
            </li>
            {filtered.length > 0 ? (
              filtered.map((p) => (
                <li
                  key={p.ProjectId}
                  onClick={() => select(p.ProjectId)}
                  className={`px-4 py-2 text-sm cursor-pointer transition-colors ${
                    value === p.ProjectId
                      ? "bg-[#08757c]/10 text-[#08757c] font-semibold"
                      : "hover:bg-[#f1f4f4] text-[#181c1d]"
                  }`}
                >
                  <span className="font-medium text-[#08757c]">
                    {p.ProjectCode}
                  </span>
                  <span className="text-[#3e494a]"> — {p.ProjectName}</span>
                </li>
              ))
            ) : (
              <li className="px-4 py-3 text-xs text-[#6e797a] text-center">
                No projects found
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Dynamic Dump Table ───────────────────────────────────────────────────────
function DumpReportTable({ rows }) {
  if (!rows.length) return null;
  const columns = Object.keys(rows[0]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-[#f1f4f4] border-b border-[#e0e3e3]">
            {columns.map((col) => (
              <th
                key={col}
                className="px-4 py-3 text-xs font-bold text-[#3e494a] uppercase tracking-wider whitespace-nowrap"
              >
                {col.replace(/_/g, " ")}
              </th>
            ))}
            {/* Filler column to absorb extra space */}
            <th className="w-full"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#e0e3e3]">
          {rows.map((row, idx) => (
            <tr key={idx} className="hover:bg-[#f1f4f4] transition-colors">
              {columns.map((col) => {
                const isRemarks = col.toLowerCase() === "remarks";
                return (
                  <td
                    key={col}
                    className={`px-4 py-3 text-xs text-[#181c1d] ${
                      isRemarks ? "min-w-[150px] whitespace-normal" : "whitespace-nowrap"
                    }`}
                  >
                    {row[col] ?? "-"}
                  </td>
                );
              })}
              <td></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function LabUtilizationReport() {
  const dispatch = useDispatch();

  const [projID, setProjID] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const [projects, setProjects] = useState([]);
  const [records, setRecords] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [projError, setProjError] = useState("");

  const [mode, setMode] = useState("details"); // "details" | "dump"
  const [dumpFromDate, setDumpFromDate] = useState("");
  const [dumpToDate, setDumpToDate] = useState("");
  const [dumpDateError, setDumpDateError] = useState("");
  const [dumpLoading, setDumpLoading] = useState(false);
  const [dumpRows, setDumpRows] = useState([]);
  const [dumpGenerated, setDumpGenerated] = useState(false);

  async function handleDumpGenerate() {
    if (!dumpFromDate || !dumpToDate) {
      setDumpDateError("From and To date are required.");
      return;
    }
    setDumpDateError("");
    setDumpLoading(true);
    try {
      const result = await dispatch(
        fetchSetupUtilDumpReport({
          fromDate: dumpFromDate,
          toDate: dumpToDate,
          companyId: companyID,
        }),
      ).unwrap();

    const rows = Array.isArray(result)
  ? result
  : Array.isArray(result?.data)
    ? result.data
    : Array.isArray(result?.data?.data)
      ? result.data.data
      : [];
    setDumpRows(rows);
    setDumpGenerated(true);
    } catch {
      setDumpRows([]);
      setDumpGenerated(true);
      setDumpDateError("Something went wrong generating the report.");
    } finally {
      setDumpLoading(false);
    }
  }

  function handleDumpClear() {
    setDumpFromDate("");
    setDumpToDate("");
    setDumpDateError("");
    setDumpRows([]);
    setDumpGenerated(false);
  }


  const fetchDumpData = useCallback(
  async (from, to) => {
    setDumpDateError("");
    setDumpLoading(true);
    try {
      const result = await dispatch(
        fetchSetupUtilDumpReport({
          fromDate: from,
          toDate: to,
          companyId: companyID,
        }),
      ).unwrap();

      const rows = Array.isArray(result)
        ? result
        : Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result?.data?.data)
            ? result.data.data
            : [];
      setDumpRows(rows);
      setDumpGenerated(true);
    } catch {
      setDumpRows([]);
      setDumpGenerated(true);
      setDumpDateError("Something went wrong generating the report.");
    } finally {
      setDumpLoading(false);
    }
  },
  [dispatch],
);


useEffect(() => {
  if (!dumpFromDate || !dumpToDate) return;
  fetchDumpData(dumpFromDate, dumpToDate);
}, [dumpFromDate, dumpToDate, fetchDumpData]);

  // Export data derived from whatever columns actually came back
  const dumpExportHeaders = dumpRows.length ? Object.keys(dumpRows[0]) : [];
  const dumpExportData = dumpRows.map((row) =>
    dumpExportHeaders.map((h) => row[h]),
  );

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  const fetchData = useCallback(
    async (page, size, selectedProjID, from, to) => {
      setLoading(true);
      try {
        const start = (page - 1) * size;
        const result = await dispatch(
          fetchSetupUtilReportData({
            projID: selectedProjID,
            fromDate: from,
            toDate: to,
            start,
            length: size,
            sortColumn: 0,
            sortColumnDir: "desc",
            search: "",
            draw: String(page),
            companyId: companyID,
          }),
        ).unwrap();

        const responseData = result?.data;
        setRecords(responseData?.data ?? []);
        setTotalRecords(responseData?.recordsTotal ?? 0);
      } catch {
        setRecords([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    },
    [dispatch],
  );

  // load projects dropdown + initial report data on mount
  useEffect(() => {
    dispatch(fetchSetupUtilProjectList({ projectId: 0, companyId: companyID }))
      .unwrap()
      .then((res) => setProjects(res?.data ?? []))
      .catch(() => {});
    fetchData(1, pageSize, 0, "", "");
  }, []);

  // Auto-fetch data when project, fromDate, or toDate changes
  useEffect(() => {
    if (projID === 0) return; // Don't auto-fetch if no specific project selected
    setCurrentPage(1);
    fetchData(1, pageSize, projID, fromDate, toDate);
  }, [projID, fromDate, toDate]);

  function handlePageChange(page) {
    setCurrentPage(page);
    fetchData(page, pageSize, projID, fromDate, toDate);
  }

  function handlePageSizeChange(size) {
    setPageSize(size);
    setCurrentPage(1);
    fetchData(1, size, projID, fromDate, toDate);
  }

  const exportHeaders = [
    "Project No.",
    "Facility Name",
    "Test Type",
    "Test Name",
    "Billable Hrs",
    "Non-Billable Hrs",
    "Total Hrs",
    "Start Date",
    "Start Time",
    "End Date",
    "End Time",
    "Temp Range",
    "Humidity Range",
  ];
  const exportData = records.map((r) => [
    r.ProjectName,
    r.FacilityName,
    r.TestTypeName,
    r.TestName,
    r.BilledHrs,
    r.NonBilled,
    r.TotalHrs,
    r.StartDate,
    r.StartTime,
    r.EndDate,
    r.EndTime,
    r.TemperatureRange,
    r.HumidityRange,
  ]);

  // Derived summary stats from current page records
  const totalBillable = records
    .reduce((s, r) => s + (r.BilledHrs ?? 0), 0)
    .toFixed(1);
  const totalHrs = records.reduce((s, r) => s + (r.TotalHrs ?? 0), 0);
  const utilization =
    totalHrs > 0
      ? (
          (records.reduce((s, r) => s + (r.BilledHrs ?? 0), 0) / totalHrs) *
          100
        ).toFixed(1)
      : "0.0";
  const activeProjects = new Set(records.map((r) => r.ProjectName)).size;

  return (
    <DashboardLayout>
      <div className="p-6 space-y-4">
        {/* Page Title */}
        <h1 className="text-xl font-bold tracking-tight text-[#08757c]">
          Setup Utilization Report
        </h1>

        <div className="flex gap-1 bg-white p-1.5 rounded-2xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/40 w-fit">
          <button
            onClick={() => setMode("details")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${
              mode === "details"
                ? "bg-[#08757c] text-white shadow-md"
                : "text-[#3e494a] hover:bg-[#f1f4f4]"
            }`}
          >
            <span className="material-symbols-outlined text-[18px]">
              table_rows
            </span>
            Report Details
          </button>
          <button
            onClick={() => setMode("dump")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${
              mode === "dump"
                ? "bg-[#08757c] text-white shadow-md"
                : "text-[#3e494a] hover:bg-[#f1f4f4]"
            }`}
          >
            <span className="material-symbols-outlined text-[18px]">
              database
            </span>
            Dump Report
          </button>
        </div>
        {mode === "details" && (
          <>
            {/* Filters */}
            <section className="bg-white rounded-xl p-5 shadow-sm">
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[280px]">
                  <label className="block text-xs font-semibold text-[#3e494a] mb-1.5">
                    Project Selection <span className="text-red-500">*</span>
                  </label>
                  <ProjectDropdown
                    projects={projects}
                    value={projID}
                    onChange={(id) => {
                      setProjID(id);
                      setProjError("");
                    }}
                  />
                  {projError && (
                    <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                      <span className="material-symbols-outlined text-[14px]">
                        error
                      </span>
                      {projError}
                    </p>
                  )}
                </div>

                <div className="w-48">
                  <label className="block text-xs font-semibold text-[#3e494a] mb-1.5">
                    From Date
                  </label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);
                      setToDate("");
                    }}
                    onKeyDown={(e) => e.preventDefault()}
                    onClick={(e) => e.currentTarget.showPicker?.()}
                    className="w-full px-4 py-2.5 bg-[#f1f4f4] border-none rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#08757c] transition-all cursor-pointer"
                  />
                </div>

                <div className="w-48">
                  <label className="block text-xs font-semibold text-[#3e494a] mb-1.5">
                    To Date
                  </label>
                  <input
                    type="date"
                    value={toDate}
                    min={fromDate || undefined}
                    onChange={(e) => setToDate(e.target.value)}
                    onKeyDown={(e) => e.preventDefault()}
                    onClick={(e) => e.currentTarget.showPicker?.()}
                    className="w-full px-4 py-2.5 bg-[#f1f4f4] border-none rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#08757c] transition-all cursor-pointer"
                  />
                </div>

                <div
                  title={!projID ? "Select the project first" : ""}
                  onClick={(e) => {
                    if (!projID) {
                      e.stopPropagation();
                      setProjError("Project selection is required.");
                    }
                  }}
                >
                  <ExportExcel
                    headers={exportHeaders}
                    data={exportData}
                    filename="lab_utilization_report"
                    buttonLabel="Export Excel"
                    disabled={records.length === 0 || !projID}
                  />
                </div>
              </div>
            </section>

            {/* Report Table */}
            <section className="bg-white rounded-xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#f1f4f4] border-b border-[#e0e3e3]">
                      <th className="px-5 py-2.5 text-xs font-bold text-[#3e494a] uppercase tracking-wider">
                        Project No.
                      </th>
                      <th className="px-5 py-2.5 text-xs font-bold text-[#3e494a] uppercase tracking-wider">
                        Facility &amp; Test Type
                      </th>
                      <th
                        className="px-5 py-2.5 text-xs font-bold text-[#3e494a] uppercase tracking-wider text-center"
                        colSpan={3}
                      >
                        Hours Tracker
                      </th>
                      <th className="px-5 py-2.5 text-xs font-bold text-[#3e494a] uppercase tracking-wider">
                        Duration Schedule
                      </th>
                      <th className="px-5 py-2.5 text-xs font-bold text-[#3e494a] uppercase tracking-wider">
                        Env. Conditions
                      </th>
                    </tr>
                    <tr className="bg-[#f1f4f4]/50 border-b border-[#e0e3e3] text-[10px]">
                      <th className="px-5 py-1.5" />
                      <th className="px-5 py-1.5" />
                      <th className="px-2 py-1.5 text-center text-[#3e494a]/70">
                        Billable
                      </th>
                      <th className="px-2 py-1.5 text-center text-[#3e494a]/70">
                        Non-Bill
                      </th>
                      <th className="px-2 py-1.5 text-center font-bold text-[#08757c]">
                        Total
                      </th>
                      <th className="px-5 py-1.5" />
                      <th className="px-5 py-1.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e0e3e3]">
                    {loading ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center">
                          <div className="flex items-center justify-center gap-2 text-[#08757c]">
                            <span className="material-symbols-outlined animate-spin">
                              progress_activity
                            </span>
                            <span className="text-sm">
                              Loading report data...
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : records.length > 0 ? (
                      records.map((record, idx) => (
                        <ReportRow key={idx} record={record} />
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-6 py-12 text-center text-sm text-[#3e494a]"
                        >
                          No records found. Adjust your filters and generate the
                          report.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination Footer */}
              <footer className="flex items-center justify-between px-5 py-3 bg-[#f1f4f4]">
                <div className="flex items-center gap-4">
                  <span className="text-xs text-[#3e494a]">
                    Total Records:{" "}
                    <strong className="text-[#181c1d]">{totalRecords}</strong>
                  </span>
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] uppercase font-bold text-[#6e797a]">
                      Show
                    </label>
                    <select
                      value={pageSize}
                      onChange={(e) =>
                        handlePageSizeChange(Number(e.target.value))
                      }
                      className="bg-white border-none text-xs rounded-md py-1 px-2 focus:ring-0 cursor-pointer"
                    >
                      {PAGE_SIZE_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </footer>
            </section>
          </>
        )}

        {mode === "dump" && (
          <>
            <section className="bg-white rounded-xl p-5 shadow-sm">
              <div className="flex flex-wrap items-end gap-4">
                <div className="w-48">
                  <label className="block text-xs font-semibold text-[#3e494a] mb-1.5">
                    From Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={dumpFromDate}
                    onChange={(e) => {
                      setDumpFromDate(e.target.value);
                      setDumpToDate("");
                    }}
                    onKeyDown={(e) => e.preventDefault()}
                    onClick={(e) => e.currentTarget.showPicker?.()}
                    className="w-full px-4 py-2.5 bg-[#f1f4f4] border-none rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#08757c] cursor-pointer"
                  />
                </div>

                <div className="w-48">
                  <label className="block text-xs font-semibold text-[#3e494a] mb-1.5">
                    To Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={dumpToDate}
                    min={dumpFromDate || undefined}
                    onChange={(e) => setDumpToDate(e.target.value)}
                    onKeyDown={(e) => e.preventDefault()}
                    onClick={(e) => e.currentTarget.showPicker?.()}
                    className="w-full px-4 py-2.5 bg-[#f1f4f4] border-none rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#08757c] cursor-pointer"
                  />
                </div>

                <button
                  onClick={handleDumpClear}
                  className="px-5 py-2.5 bg-[#f1f4f4] text-[#3e494a] text-sm font-semibold rounded-lg"
                >
                  Clear
                </button>

                <ExportExcel
                  headers={dumpExportHeaders}
                  data={dumpExportData}
                  filename="setup_utilization_dump_report"
                  buttonLabel="Export Excel"
                  disabled={dumpRows.length === 0}
                />
              </div>

              {dumpDateError && (
                <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
                  <span className="material-symbols-outlined text-[14px]">
                    error
                  </span>
                  {dumpDateError}
                </p>
              )}
            </section>

            <section className="bg-white rounded-xl overflow-hidden shadow-sm">
              {dumpLoading ? (
                <div className="flex items-center justify-center gap-2 text-[#08757c] py-12">
                  <span className="material-symbols-outlined animate-spin">
                    progress_activity
                  </span>
                  <span className="text-sm">Loading report data...</span>
                </div>
              ) : dumpRows.length > 0 ? (
                <>
                  <DumpReportTable rows={dumpRows} />
                  <footer className="flex items-center px-5 py-3 bg-[#f1f4f4]">
                    <span className="text-xs text-[#3e494a]">
                      Total Records:{" "}
                      <strong className="text-[#181c1d]">
                        {dumpRows.length}
                      </strong>
                    </span>
                  </footer>
                </>
              ) : (
                <div className="px-6 py-12 text-center text-sm text-[#3e494a]">
                  {dumpGenerated
                    ? "No records found for the selected date range."
                    : "Select a date range and click Generate to view the report."}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
